// app/providers.tsx
"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, Suspense } from "react";
import { usePostHog } from "posthog-js/react";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import appConfig from "../app.config";

export function PostHogProvider({ children, isEnabled }: { isEnabled: boolean, children: React.ReactNode }) {
  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com",
      person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
      capture_pageview: false, // Disable automatic pageview capture, as we capture manually
    });
  }, []);

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView isEnabled={isEnabled} />
      {children}
    </PHProvider>
  );
}

function PostHogPageView({ isEnabled }: { isEnabled: boolean }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  // Track pageviews
  useEffect(() => {
    if (isEnabled && pathname && posthog ) {
      let url = window.origin + pathname;
      if (searchParams.toString()) {
        url = url + "?" + searchParams.toString();
      }

      posthog.capture("$pageview", {
        $current_url: url,
        // TODO: This is duplicated here and in the AnalyticsProvider
        environment: appConfig.analytics.environment,
        version: appConfig.version,
      });
    }
  }, [isEnabled, pathname, searchParams, posthog]);

  return null;
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView({ isEnabled }: { isEnabled: boolean }) {
  return (
    <Suspense fallback={null}>
      <PostHogPageView isEnabled={isEnabled} />
    </Suspense>
  );
}
